import React from 'react';

import cognitoAuthService from '../../services/cognitoAuthService';
import { FILLS, FONTS, RADII, SIZING, STROKES } from '../../styles/tokens';
import {
  type PendingSignInAction,
  discardPendingSignInAction,
  storePendingSignInAction,
} from '../../utils/pendingSignInAction';
import { captureCurrentLocation } from '../../utils/postLoginRedirect';

/*
 * Google brand colours below are fixed by Google's Sign-In branding guidelines
 * and are intentionally exempt from the token/hex-lint rule for this block only.
 * This is the single home for the Google-branded sign-in button, rendered by the sign-in pop-up.
 */
/* eslint-disable no-restricted-syntax -- Google-mandated brand hex colours */
const GOOGLE_LOGO_BLUE = '#4285F4';
const GOOGLE_LOGO_GREEN = '#34A853';
const GOOGLE_LOGO_YELLOW = '#FBBC05';
const GOOGLE_LOGO_RED = '#EA4335';
const GOOGLE_BUTTON_TEXT = '#1F1F1F';
const GOOGLE_BUTTON_BORDER = '#747775';
/* eslint-enable no-restricted-syntax */

const GoogleGlyph: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
    <path
      fill={GOOGLE_LOGO_BLUE}
      d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"
    />
    <path
      fill={GOOGLE_LOGO_GREEN}
      d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"
    />
    <path
      fill={GOOGLE_LOGO_YELLOW}
      d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z"
    />
    <path
      fill={GOOGLE_LOGO_RED}
      d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"
    />
  </svg>
);

interface GoogleSignInButtonProps {
  /** Visible button text — e.g. "Continue with Google" / "Sign up with Google". */
  label: string;
  /** Minimum button height; defaults to the standard control height. */
  minHeight?: string;
  /** The guest's pending action, kept across the redirect so it can finish on return. */
  actionAfterSignIn?: PendingSignInAction;
  /** Whether a click saves the current page as the return-to path. Defaults to true. */
  captureReturnTo?: boolean;
}

/**
 * White Google-branded sign-in button that starts the hosted-UI OAuth redirect. When given an
 * action it is stored first, so the page the guest returns to can finish it; with none, any
 * stored action is dropped.
 */
const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({
  label,
  minHeight = SIZING.controlHeight,
  actionAfterSignIn,
  captureReturnTo = true,
}) => (
  <button
    type="button"
    onClick={() => {
      if (captureReturnTo) captureCurrentLocation();
      if (actionAfterSignIn) storePendingSignInAction(actionAfterSignIn);
      else discardPendingSignInAction();
      void cognitoAuthService.beginGoogleSignIn();
    }}
    style={{
      width: '100%',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '12px',
      minHeight,
      padding: '10px 16px',
      // eslint-disable-next-line no-restricted-syntax -- Google brand guidelines mandate a white button background
      background: '#FFFFFF',
      border: `1px solid ${GOOGLE_BUTTON_BORDER}`,
      borderRadius: RADII.control,
      color: GOOGLE_BUTTON_TEXT,
      fontFamily: FONTS.body,
      fontSize: '14px',
      fontWeight: 500,
      cursor: 'pointer',
    }}
  >
    <GoogleGlyph />
    {label}
  </button>
);

/** "or" separator that pairs with the Google button in the sign-in pop-up. */
export const OrDivider: React.FC<{ label?: string }> = ({ label = 'or' }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      margin: '20px 0',
      color: FILLS.textMuted,
      fontFamily: FONTS.body,
      fontSize: '12px',
    }}
  >
    <span style={{ flex: 1, height: '1px', background: STROKES.subtle }} />
    {label}
    <span style={{ flex: 1, height: '1px', background: STROKES.subtle }} />
  </div>
);

export default GoogleSignInButton;
